import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { AdminShell } from "@/components/Layout";
import { AdminJobDetailBody } from "@/pages/Admin";
import BrandedHero from "@/components/BrandedHero";
import WoAnswerSheet from "@/components/WoAnswerSheet";
import LegalProtocolPanel from "@/components/LegalProtocolPanel";
import AskTenantPanel from "@/components/AskTenantPanel";
import StatusFlag from "@/components/StatusFlag";
import { api, API, mediaSrc } from "@/lib/api";
import PhotoMarkupModal from "@/components/PhotoMarkupModal";
import BidDrafterModal from "@/components/BidDrafterModal";
import PinToSweeperButton from "@/components/PinToSweeperButton";
import PropertyEnrichCard from "@/components/PropertyEnrichCard";
import PastWosAtUnitStrip from "@/components/PastWosAtUnitStrip";
import { PushPin } from "@phosphor-icons/react";
import { BeforeAfterSlider } from "@/components/BeforeAfterSlider";
import { buildBeforeAfterComparison } from "@/lib/buildBeforeAfterComparison";
import InspectRadarGrid from "@/components/InspectRadarGrid";
import { toast } from "sonner";

/**
 * AdminJobDetailV2 — Contractor-Plus-style detail page, wired to real WO data.
 *
 * NEW body layout (matches the preview mockup you approved):
 *   LEFT (2/3):  Description · Photos gallery (before/after) · Checklist ·
 *                Work Performed · Receipts · Notes thread + composer
 *   RIGHT (1/3): Quick Actions · Details · Timing · Rebill preview
 *
 * FOOTER: Collapsible "Show classic view" that renders V1's <AdminJobDetailBody/>
 * for anyone who wants every action button that hasn't been redesigned yet.
 *
 * Escape hatch: `?v=classic` still routes to V1 (AdminJobDetail).
 */

const priColors = { critical: "#B91C1C", urgent: "#E64C5A", high: "#E64C5A", med: "#F5B93F", medium: "#F5B93F", low: "#8B93A7", normal: "#8B93A7" };

export default function AdminJobDetailV2() {
  const { id } = useParams();
  const nav = useNavigate();
  const [job, setJob] = useState(null);
  const [photoTab, setPhotoTab] = useState("all");
  const [lightbox, setLightbox] = useState(null);
  const [markupPhoto, setMarkupPhoto] = useState(null); // photo object being annotated
  const [baOpen, setBaOpen] = useState(false); // before/after slider modal
  const [pdfBusy, setPdfBusy] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const [shareResult, setShareResult] = useState(null); // { token, url, expires_at, fullUrl }
  const [timelapseOpen, setTimelapseOpen] = useState(false);
  const [bidDrafterOpen, setBidDrafterOpen] = useState(false);
  const [gridInspection, setGridInspection] = useState(null); // "move_out" | "move_in" | "periodic"
  const downloadPhotoPdf = async () => {
    setPdfBusy(true);
    try {
      const t = localStorage.getItem("access_token");
      const r = await fetch(`${API}/jobs/${id}/photos.pdf`, {
        headers: { Authorization: `Bearer ${t || ""}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `WO-photos.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("Photo PDF downloaded");
    } catch (e) {
      toast.error("Couldn't generate PDF — try again in a moment");
    } finally { setPdfBusy(false); }
  };
  const sharePhotos = async () => {
    setShareBusy(true);
    try {
      const { data } = await api.post(`/jobs/${id}/photos/share`, { days_valid: 30, include_captions: true });
      const fullUrl = `${window.location.origin}${data.url}`;
      try { await navigator.clipboard.writeText(fullUrl); } catch {}
      // Open the share result modal for the "Email to landlord" flow.
      setShareResult({ ...data, fullUrl });
      toast.success("Public link copied — also opened share options");
    } catch (e) {
      toast.error("Couldn't create share link");
    } finally { setShareBusy(false); }
  };
  const [noteDraft, setNoteDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [weatherSince, setWeatherSince] = useState(null);
  const [syncingCU, setSyncingCU] = useState(false);
  // Batch-relabel photos: when true, each photo tile becomes selectable and
  // a "Move to → Before / After" action bar appears at the bottom of the card
  const [photoSelectMode, setPhotoSelectMode] = useState(false);
  const [selectedPhotoIds, setSelectedPhotoIds] = useState([]);
  const [relabeling, setRelabeling] = useState(false);
  const [workers, setWorkers] = useState([]);

  useEffect(() => {
    api.get("/assignable-users").then(r => setWorkers(r.data || [])).catch(() => setWorkers([]));
  }, []);

  const reload = () =>
    api.get(`/jobs/${id}`)
      .then(r => setJob(r.data))
      .catch(() => setJob(null));

  useEffect(() => { reload(); }, [id]);

  // For completed WOs, pull "days since + rain days" so the hero can show a
  // proof-of-work chain-of-custody chip ("3 days · 2 wet · roof still holding").
  useEffect(() => {
    if (!job?.completed_at) { setWeatherSince(null); return; }
    let cancelled = false;
    api.get(`/weather/since?job_id=${id}`)
      .then(r => { if (!cancelled) setWeatherSince(r.data); })
      .catch(() => { if (!cancelled) setWeatherSince(null); });
    return () => { cancelled = true; };
  }, [id, job?.completed_at]);

  const photos = useMemo(() => {
    if (!job) return [];
    const before = (job.before_photos || []).map(p => ({ ...normPhoto(p), kind: "before" }));
    const after  = (job.after_photos  || []).map(p => ({ ...normPhoto(p), kind: "after"  }));
    return [...before, ...after];
  }, [job]);

  const shownPhotos = useMemo(() => {
    if (photoTab === "all") return photos;
    return photos.filter(p => p.kind === photoTab);
  }, [photos, photoTab]);

  const checklist = job?.checklist || [];
  const doneCount = checklist.filter(c => c.done).length;
  const notes = job?.notes || [];
  const receipts = job?.receipts || [];

  const timing = useMemo(() => buildTiming(job), [job]);
  const activity = useMemo(() => buildActivity(job), [job]);

  const postNote = async () => {
    const text = noteDraft.trim();
    if (!text) return;
    setPosting(true);
    try {
      await api.post(`/jobs/${id}/notes`, { text });
      setNoteDraft("");
      reload();
    } catch (e) {
      alert("Failed to post note");
    } finally { setPosting(false); }
  };

  const toggleChecklistItem = async (item) => {
    setBusy(true);
    try {
      await api.patch(`/jobs/${id}/checklist/${item.id}?force=true`, { done: !item.done });
      reload();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to update checklist");
    } finally { setBusy(false); }
  };

  const sms = () => job?.customer_phone && (window.location.href = `sms:${job.customer_phone}`);
  const call = () => job?.customer_phone && (window.location.href = `tel:${job.customer_phone}`);
  const directions = () => job?.property_address && window.open(
    `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(job.property_address + (job.unit ? " Unit " + job.unit : "") + " Philadelphia, PA")}`, "_blank"
  );

  const changeStatus = async (newStatus) => {
    if (!job || job.status === newStatus) return;
    setBusy(true);
    try {
      if (newStatus === "completed" || newStatus === "follow_up") {
        // Terminal statuses go through the completion endpoint so
        // completed_at / clock_out_at / follow-up reason all fire.
        // Admins bypass the worker photo/receipt/notes gates.
        await api.post(`/jobs/${id}/complete`, {
          status: newStatus,
          notes: "",
          follow_up_reason: newStatus === "follow_up" ? "other" : undefined,
        });
      } else {
        // assigned / in_progress → PATCH
        await api.patch(`/jobs/${id}`, { status: newStatus });
      }
      reload();
    } catch (e) {
      alert(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Failed to change status");
    } finally { setBusy(false); }
  };

  const uploadPhotos = async (files, kind = "before") => {
    if (!files || files.length === 0) return;
    setBusy(true);
    let uploaded = 0;
    const errors = [];
    try {
      for (const file of files) {
        try {
          const fd = new FormData();
          fd.append("file", file);
          fd.append("kind", kind);
          await api.post(`/jobs/${id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          uploaded += 1;
        } catch (e) {
          errors.push({ name: file.name || "file",
                        detail: e?.response?.data?.detail || e?.message || "upload failed" });
        }
      }
      if (uploaded > 0) toast.success(`✓ Uploaded ${uploaded} ${uploaded === 1 ? "file" : "files"}`);
      errors.forEach(err => toast.error(`${err.name}: ${err.detail}`));
      reload();
    } finally { setBusy(false); }
  };

  // Bulk re-tag N photos to before or after — fires change-kind for each in
  // parallel then reloads. Preserves any that were already in the target
  // bucket (the endpoint returns moved:false for those).
  const batchRelabel = async (toKind) => {
    if (selectedPhotoIds.length === 0 || relabeling) return;
    setRelabeling(true);
    try {
      const results = await Promise.all(selectedPhotoIds.map(fid =>
        api.post(`/jobs/${id}/photos/change-kind`, { file_id: fid, to_kind: toKind })
          .then(r => ({ ok: true, moved: r.data?.moved }))
          .catch(err => ({ ok: false, err }))
      ));
      const moved = results.filter(r => r.ok && r.moved).length;
      const already = results.filter(r => r.ok && !r.moved).length;
      const failed = results.filter(r => !r.ok).length;
      const parts = [];
      if (moved) parts.push(`${moved} moved to ${toKind}`);
      if (already) parts.push(`${already} already ${toKind}`);
      if (failed) parts.push(`${failed} failed`);
      (failed ? toast.error : toast.success)(parts.join(" · ") || "Nothing to change");
      setSelectedPhotoIds([]);
      setPhotoSelectMode(false);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Batch relabel failed");
    } finally { setRelabeling(false); }
  };

  // Hero background photo — pick the first "after" photo (proof-of-work) or
  // fall back to the first "before" photo. Must be declared BEFORE the early
  // `if (!job) return` so React hook order stays stable across renders.
  const heroPhoto = useMemo(() => {
    const after = photos.find(p => p.kind === "after");
    if (after) return after.url;
    if (photos.length > 0) return photos[0].url;
    return null;
  }, [photos]);

  if (!job) {
    return (
      <AdminShell>
        <BrandedHero title="Loading…" size="sm"/>
      </AdminShell>
    );
  }

  const chips = [];
  chips.push({ label: job.status || "—" });
  if (job.priority && priColors[job.priority]) {
    chips.push({ icon: "🔥", label: `${job.priority} priority` });
  }
  if (job.habitability || job.life_safety) chips.push({ icon: "⚠️", label: "Life-Safety" });
  if (job.appfolio_link) chips.push({ label: "AppFolio" });
  if (job.clickup_task_id) chips.push({ label: "ClickUp" });
  if (job.portfolio_name) chips.push({ label: job.portfolio_name });
  // Weather chain-of-custody (only for completed jobs)
  if (weatherSince && weatherSince.days_since > 0) {
    const rainNote = weatherSince.rain_days > 0
      ? `${weatherSince.rain_days} wet day${weatherSince.rain_days === 1 ? "" : "s"}`
      : "no rain";
    chips.push({ icon: "🌤️", label: `${weatherSince.days_since}d since · ${rainNote}` });
  }
  if (photos.length > 0) chips.push({ icon: "📸", label: `${photos.length} photo${photos.length === 1 ? "" : "s"}` });

  return (
    <AdminShell>
      <div className="relative" data-testid="v2-hero-shell">
        {heroPhoto && (
          <div
            aria-hidden="true"
            data-testid="v2-hero-photo-backdrop"
            className="absolute inset-x-0 top-0 h-[280px] sm:h-[340px] bg-cover bg-center pointer-events-none z-0 -mx-4 sm:-mx-6 lg:-mx-8"
            style={{
              backgroundImage: `url(${heroPhoto})`,
              opacity: 0.35,
              maskImage: "linear-gradient(to bottom, black 0%, black 60%, transparent 100%)",
              WebkitMaskImage: "linear-gradient(to bottom, black 0%, black 60%, transparent 100%)",
            }}
          />
        )}
        <div className="relative z-10">
          <BrandedHero
        breadcrumbs={[
          { to: "/admin?v=v2", label: "← Dashboard" },
          { to: "/admin/jobs", label: "Work Orders" },
          { label: job.work_order_number ? `WO# ${job.work_order_number}` : `#${id.slice(0,8)}` },
        ]}
        eyebrow={job.work_order_number ? `WO# ${job.work_order_number}` : undefined}
        title={job.title || "Work Order"}
        subtitle={
          <>
            📍{" "}
            {job.property_address ? (
              <Link to={`/admin/properties/${encodeURIComponent(job.property_address)}`} className="hover:underline">
                {job.property_address}
              </Link>
            ) : "—"}
            {job.unit && <> · Apt {job.unit}</>}
            {job.tenant_name && <> · {job.tenant_name}</>}
          </>
        }
        chips={chips}
        size="md"
        actions={
          <div className="flex items-center gap-2">
            {(job.is_turn || job.inspection_type) && (
              <button onClick={() => setGridInspection(job.inspection_type === "move_in" ? "move_in" : (job.inspection_type === "periodic" ? "periodic" : "move_out"))}
                data-testid="v2-wo-grid-btn"
                className="h-9 px-3 rounded-full bg-fuchsia-500 hover:bg-fuchsia-600 text-white text-[10px] font-black uppercase tracking-widest border border-fuchsia-700 inline-flex items-center gap-1">
                🪟 Grid
              </button>
            )}
            <Link to={`/admin/jobs/${id}?v=classic`} data-testid="v2-wo-classic-toggle"
              className="h-9 px-3 rounded-full bg-white/15 backdrop-blur text-white text-[10px] font-black uppercase tracking-widest hover:bg-white/25 border border-white/20 inline-flex items-center">
              Use Classic
            </Link>
          </div>
        }
      />
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 -mt-16 relative z-10 pb-6">
        {/* Pinned WO Answer Sheet — Complaint / Human Notes / What was done /
            Who-Status-Address. System sync lines get collapsed below.
            Sits above everything (enrich, past WOs, description) per user spec. */}
        <WoAnswerSheet job={job} onRefresh={reload}/>

        {/* Legal Protocol panel — auto-shows on leak/flood/mold/water-intrusion
            WOs. Checklist + moisture readings + SLA. The component self-fetches
            protocol state and renders nothing if the job has no protocol,
            so we can leave it mounted for every WO. */}
        <div className="mt-3">
          <LegalProtocolPanel jobId={id}/>
        </div>
        <AskTenantPanel jobId={id} job={job}/>

        {/* AI enrich strip — same green pull-from-web as unit page.
            Renders compact ("dense") so it doesn't dominate the job view.
            Applies to the job's property/unit so a lookup here fills the
            whole address, not just this WO. */}
        {job.property_address && (
          <div className="mb-4" data-testid="v2wo-enrich">
            <PropertyEnrichCard address={job.property_address} unit={job.unit} dense={true}/>
          </div>
        )}
        {/* Past WOs at this same unit — instant repair history without
            navigating away. Auto-hides if none. */}
        {job.property_address && job.unit && (
          <div className="mb-4">
            <PastWosAtUnitStrip address={job.property_address} unit={job.unit} excludeJobId={id}/>
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* ═══ LEFT ═══════════════════════════════════ */}
          <div className="lg:col-span-2 space-y-4">
            {/* Description */}
            <Card testId="v2body-description">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-lg font-black text-[#0F1F3A]">Description</h3>
                <StatusFlag status={job.status} size="sm"/>
                {job.category && (
                  <span className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-slate-100 text-[#0F1F3A]">
                    {job.category}
                  </span>
                )}
                {job.source && (
                  <span className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#F5B93F]/20 text-[#8B6D1F]">{job.source}</span>
                )}
              </div>
              <p className="text-sm text-[#0F1F3A] leading-relaxed whitespace-pre-wrap">
                {job.description || <em className="text-[#8B93A7]">No description provided.</em>}
              </p>
              {job.tech_notes && (
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <div className="text-[9px] font-black uppercase tracking-widest text-[#8B93A7] mb-1">Tech Notes</div>
                  <p className="text-sm text-[#0F1F3A] whitespace-pre-wrap">{job.tech_notes}</p>
                </div>
              )}
            </Card>

            {/* Notes thread — hoisted to top-of-column per user request */}
            <Card testId="v2body-notes">
              <div className="flex items-center gap-2 mb-3">
                <h3 className="text-lg font-black text-[#0F1F3A]">Notes &amp; Updates</h3>
                <span className="text-[10px] font-black uppercase tracking-widest text-[#8B93A7]">{notes.length}</span>
              </div>
              <div className="mb-4 bg-slate-50 rounded-xl p-3">
                <textarea
                  value={noteDraft}
                  onChange={e => setNoteDraft(e.target.value)}
                  placeholder="Add a note · ⌘Enter to post"
                  rows={2}
                  className="w-full bg-transparent text-sm text-[#0F1F3A] placeholder:text-[#8B93A7] focus:outline-none resize-none"
                  data-testid="v2body-note-input"
                  onKeyDown={e => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") postNote(); }}
                />
                <div className="mt-2 flex items-center gap-1.5">
                  <span className="text-[10px] text-[#8B93A7] font-medium">
                    Posts to WO note history — visible to all techs & office
                  </span>
                  <button
                    onClick={postNote}
                    disabled={posting || !noteDraft.trim()}
                    data-testid="v2body-note-post"
                    className="ml-auto h-7 px-3 rounded-full bg-[#0F1F3A] text-white text-[10px] font-black uppercase tracking-widest hover:bg-[#1a2d4f] disabled:opacity-50 disabled:cursor-not-allowed">
                    {posting ? "Posting…" : "Post"}
                  </button>
                </div>
              </div>
              {notes.length === 0 ? (
                <div className="text-center text-[11px] text-[#8B93A7] py-4 font-medium">
                  No notes yet — be the first to add one above
                </div>
              ) : (
                <div className="space-y-3">
                  {[...notes].reverse().slice(0, 5).map((n, i) => {
                    const authorName = noteAuthorLabel(n);
                    return (
                    <div key={n.id || i} data-testid={`v2body-note-${i}`}
                      className="flex gap-3 p-3 rounded-xl hover:bg-slate-50">
                      <div className={`h-9 w-9 rounded-full flex items-center justify-center text-white font-black text-xs flex-shrink-0 ${noteAvatarBg(n)}`}>
                        {noteAvatarContent(n)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-sm font-black text-[#0F1F3A]">{authorName}</span>
                          {n.source && n.source !== "manual" && (
                            <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-slate-100 text-[#8B93A7]">{n.source}</span>
                          )}
                          <span className="text-[10px] font-medium text-[#8B93A7] ml-auto">{fmtTimeShort(n.at)}</span>
                        </div>
                        <div className="text-sm text-[#0F1F3A] mt-1 leading-relaxed whitespace-pre-wrap">{n.text}</div>
                      </div>
                    </div>
                    );
                  })}
                  {notes.length > 5 && (
                    <div className="text-center text-[10px] font-black uppercase tracking-widest text-[#8B93A7] pt-1">
                      +{notes.length - 5} older · open "All actions & details" to see the full thread
                    </div>
                  )}
                </div>
              )}
            </Card>

            {/* Photos */}
            <Card testId="v2body-photos">
              <div className="flex items-center gap-2 flex-wrap mb-3">
                <h3 className="text-lg font-black text-[#0F1F3A]">Photos</h3>
                <span className="text-[10px] font-black uppercase tracking-widest text-[#8B93A7]">
                  {photos.length} total
                </span>
                {photoSelectMode && (
                  <span data-testid="v2body-photo-select-count"
                    className="text-[10px] font-black uppercase tracking-widest bg-cyan-50 text-cyan-800 border border-cyan-200 px-2 py-0.5 rounded-full">
                    {selectedPhotoIds.length} selected
                  </span>
                )}
                {photos.length > 0 && (
                  <button
                    data-testid="v2body-photo-select-toggle"
                    onClick={() => {
                      if (photoSelectMode) { setPhotoSelectMode(false); setSelectedPhotoIds([]); }
                      else setPhotoSelectMode(true);
                    }}
                    className={`text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full transition-colors ${
                      photoSelectMode ? "bg-slate-900 text-white hover:bg-slate-800" : "bg-slate-100 text-[#0F1F3A] hover:bg-slate-200"
                    }`}>
                    {photoSelectMode ? "Cancel" : "Select"}
                  </button>
                )}
                {/* Landlord-wow actions — compare, PDF, share */}
                {photos.filter(p => p.kind === "before").length > 0 && photos.filter(p => p.kind === "after").length > 0 && (
                  <button onClick={() => setBaOpen(true)}
                    data-testid="v2body-photo-compare"
                    className="text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full bg-gradient-to-r from-amber-100 to-emerald-100 text-slate-800 hover:from-amber-200 hover:to-emerald-200 border border-slate-200">
                    ⇔ Compare
                  </button>
                )}
                {photos.length > 0 && (
                  <>
                    <button onClick={downloadPhotoPdf}
                      disabled={pdfBusy}
                      data-testid="v2body-photo-pdf"
                      className="text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60">
                      {pdfBusy ? "…" : "📄 PDF"}
                    </button>
                    {photos.length >= 2 && (
                      <button onClick={() => setTimelapseOpen(true)}
                        data-testid="v2body-photo-timelapse"
                        className="text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full bg-purple-600 text-white hover:bg-purple-700">
                        🎞️ Time-lapse
                      </button>
                    )}
                    <button onClick={sharePhotos}
                      disabled={shareBusy}
                      data-testid="v2body-photo-share"
                      className="text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full bg-[#00C4CC] text-[#0F1F3A] hover:bg-[#00e5ed] disabled:opacity-60">
                      {shareBusy ? "…" : "🔗 Share"}
                    </button>
                    {photos.filter(p => p.kind === "before").length > 0 && (
                      <button onClick={() => setBidDrafterOpen(true)}
                        data-testid="v2body-photo-bid-drafter"
                        className="text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full bg-fuchsia-600 text-white hover:bg-fuchsia-700">
                        ✨ Draft Bid
                      </button>
                    )}
                  </>
                )}
                <div className="ml-auto inline-flex bg-slate-100 rounded-full p-1">
                  {[
                    { k: "all",    label: `All · ${photos.length}` },
                    { k: "before", label: `Before · ${photos.filter(p => p.kind === "before").length}` },
                    { k: "after",  label: `After · ${photos.filter(p => p.kind === "after").length}` },
                  ].map(t => (
                    <button key={t.k} onClick={() => setPhotoTab(t.k)}
                      data-testid={`v2body-photo-tab-${t.k}`}
                      className={`h-7 px-3 rounded-full text-[10px] font-black uppercase tracking-widest transition-colors ${
                        photoTab === t.k ? "bg-white text-[#0F1F3A] shadow-sm" : "text-[#8B93A7] hover:text-[#0F1F3A]"
                      }`}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Select-mode shortcut row: "Select all shown" + "Select all before/after" */}
              {photoSelectMode && shownPhotos.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap mb-3 text-[10px] font-black uppercase tracking-widest">
                  <button
                    data-testid="v2body-photo-select-all-shown"
                    onClick={() => {
                      const ids = shownPhotos.map(p => p.file_id).filter(Boolean);
                      setSelectedPhotoIds(ids);
                    }}
                    className="px-2.5 py-1 rounded-full bg-slate-100 hover:bg-slate-200 text-[#0F1F3A]">
                    Select all shown
                  </button>
                  <button
                    data-testid="v2body-photo-select-all-before"
                    onClick={() => setSelectedPhotoIds(photos.filter(p => p.kind === "before").map(p => p.file_id).filter(Boolean))}
                    className="px-2.5 py-1 rounded-full bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200">
                    All before
                  </button>
                  <button
                    data-testid="v2body-photo-select-all-after"
                    onClick={() => setSelectedPhotoIds(photos.filter(p => p.kind === "after").map(p => p.file_id).filter(Boolean))}
                    className="px-2.5 py-1 rounded-full bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-200">
                    All after
                  </button>
                  <button
                    data-testid="v2body-photo-select-clear"
                    onClick={() => setSelectedPhotoIds([])}
                    className="px-2.5 py-1 rounded-full text-slate-500 hover:text-slate-800">
                    Clear
                  </button>
                </div>
              )}

              {shownPhotos.length === 0 ? (
                <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center">
                  <div className="text-3xl mb-2">📸</div>
                  <div className="text-sm font-black text-[#0F1F3A]">No {photoTab === "all" ? "" : photoTab} photos yet</div>
                  <div className="text-[10px] font-medium text-[#8B93A7] mt-1">Photos uploaded from the field will appear here</div>
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
                  {shownPhotos.map((p, i) => {
                    const isSelected = selectedPhotoIds.includes(p.file_id);
                    return (
                      <button key={p.file_id || i}
                        onClick={() => {
                          if (photoSelectMode) {
                            if (!p.file_id) return;
                            setSelectedPhotoIds(prev =>
                              prev.includes(p.file_id) ? prev.filter(x => x !== p.file_id) : [...prev, p.file_id]);
                          } else {
                            setLightbox(p);
                          }
                        }}
                        data-testid={`v2body-photo-${i}`}
                        className={`group relative aspect-square rounded-xl overflow-hidden bg-slate-100 transition-all ${
                          photoSelectMode
                            ? (isSelected ? "ring-4 ring-[#00C4CC]" : "opacity-70 hover:opacity-100 ring-2 ring-transparent hover:ring-slate-300")
                            : "hover:ring-2 hover:ring-[#00C4CC]"
                        }`}>
                        {p.media_type === "video" ? (
                          <>
                            {/* <video preload="metadata"> paints the first
                                frame — no separate poster URL required, and
                                works cross-browser on mobile Safari. */}
                            <video src={p.url} preload="metadata" muted playsInline
                              className="h-full w-full object-cover bg-slate-900"
                              onError={e => { e.target.style.display = 'none'; }}/>
                            <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                              <span className="h-10 w-10 rounded-full bg-black/60 flex items-center justify-center text-white text-lg">▶</span>
                            </span>
                          </>
                        ) : (
                          <img src={p.url} alt="" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" onError={e => { e.target.style.display = 'none'; }}/>
                        )}
                        <span className="absolute top-1.5 right-1.5 text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-white/90 text-[#0F1F3A] capitalize">
                          {p.kind}
                        </span>
                        {p.annotated && (
                          <span title="Marked-up copy" className="absolute top-1.5 left-1.5 text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#00C4CC] text-[#0F1F3A]">
                            ✏️
                          </span>
                        )}
                        {p.caption && !photoSelectMode && (
                          <div className="absolute inset-x-0 bottom-0 px-1.5 py-1 bg-gradient-to-t from-[#0F1F3A]/95 to-transparent text-[10px] font-black text-white line-clamp-2 leading-tight">
                            {p.caption}
                          </div>
                        )}
                        {!p.caption && (p.ai_tags?.length > 0) && !photoSelectMode && (
                          <div className="absolute inset-x-0 bottom-0 px-1.5 py-1 bg-gradient-to-t from-[#0F1F3A]/90 to-transparent flex flex-wrap gap-1"
                               title={p.ai_note || p.ai_tags.join(" · ")}>
                            {p.ai_tags.slice(0, 2).map((t, ti) => (
                              <span key={ti} className="text-[8px] font-black uppercase tracking-widest px-1 py-0.5 rounded bg-[#00C4CC]/90 text-[#0F1F3A]">
                                {t}
                              </span>
                            ))}
                          </div>
                        )}
                        {photoSelectMode && (
                          <span className={`absolute top-1.5 left-1.5 h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-black transition-colors ${
                            isSelected ? "bg-[#00C4CC] text-white" : "bg-white/90 text-slate-500 border border-slate-300"
                          }`}>
                            {isSelected ? "✓" : ""}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Bulk-action bar: appears only when photos are selected in select mode */}
              {photoSelectMode && selectedPhotoIds.length > 0 && (
                <div className="mt-4 flex items-center gap-2 flex-wrap p-3 rounded-xl bg-gradient-to-r from-slate-50 to-cyan-50 border border-cyan-200"
                  data-testid="v2body-photo-batch-bar">
                  <span className="text-[11px] font-black uppercase tracking-widest text-[#0F1F3A]">
                    Move {selectedPhotoIds.length} photo{selectedPhotoIds.length === 1 ? "" : "s"} to:
                  </span>
                  <button
                    disabled={relabeling}
                    data-testid="v2body-photo-batch-before"
                    onClick={() => batchRelabel("before")}
                    className="h-8 px-3 rounded-full bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-[11px] font-black uppercase tracking-widest">
                    {relabeling ? "Working…" : "→ Before"}
                  </button>
                  <button
                    disabled={relabeling}
                    data-testid="v2body-photo-batch-after"
                    onClick={() => batchRelabel("after")}
                    className="h-8 px-3 rounded-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white text-[11px] font-black uppercase tracking-widest">
                    {relabeling ? "Working…" : "→ After"}
                  </button>
                  <button
                    disabled={relabeling}
                    data-testid="v2body-photo-batch-cancel"
                    onClick={() => { setSelectedPhotoIds([]); setPhotoSelectMode(false); }}
                    className="h-8 px-3 rounded-full text-slate-500 hover:text-slate-800 ml-auto text-[10px] font-black uppercase tracking-widest">
                    Done
                  </button>
                </div>
              )}
            </Card>

            {/* Checklist */}
            {checklist.length > 0 && (
              <Card testId="v2body-checklist">
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="text-lg font-black text-[#0F1F3A]">Checklist</h3>
                  <span className="text-[10px] font-black uppercase tracking-widest text-[#8B93A7]">
                    {doneCount} of {checklist.length} · {Math.round(doneCount / checklist.length * 100)}%
                  </span>
                  <div className="ml-auto w-32 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#22C6A6] to-[#00C4CC] transition-all"
                      style={{ width: `${(doneCount / checklist.length) * 100}%` }}/>
                  </div>
                </div>
                <div className="space-y-1">
                  {checklist.map((c) => (
                    <div key={c.id} data-testid={`v2body-check-${c.id}`}
                      className="flex items-start gap-2.5 p-2 rounded-lg hover:bg-slate-50 group">
                      <button
                        onClick={() => toggleChecklistItem(c)}
                        disabled={busy}
                        className={`mt-0.5 h-5 w-5 rounded-md flex items-center justify-center flex-shrink-0 border-2 transition-colors ${
                          c.done ? "bg-[#22C6A6] border-[#22C6A6] text-white" : "border-slate-300 bg-white hover:border-[#22C6A6]"
                        }`}
                      >
                        {c.done && <span className="text-[10px] font-black">✓</span>}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-semibold ${c.done ? "text-[#8B93A7] line-through" : "text-[#0F1F3A]"}`}>
                          {c.text}
                        </div>
                        {c.notes && !c.done && (
                          <div className="text-[11px] text-[#8B93A7] mt-0.5">{c.notes}</div>
                        )}
                        {c.done && c.done_by && (
                          <div className="text-[10px] font-black uppercase tracking-widest text-[#8B93A7] mt-0.5">
                            {c.done_by} · {fmtTimeShort(c.done_at)}
                          </div>
                        )}
                      </div>
                      {c.condition && (
                        <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-slate-100 text-[#8B93A7] flex-shrink-0">
                          {c.condition}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Work Performed / Write-up */}
            {(job.work_performed || job.completion_notes || job.work_performed_polished) && (
              <Card testId="v2body-work-performed">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-black text-[#0F1F3A]">Work Performed</h3>
                  {job.work_performed_polished && (
                    <span className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#B12FBB]/15 text-[#7B2A93]">✨ Polished</span>
                  )}
                </div>
                <p className="text-sm text-[#0F1F3A] leading-relaxed whitespace-pre-wrap">
                  {job.work_performed_polished || job.work_performed || job.completion_notes}
                </p>
                {job.completion_notes && job.completion_notes !== job.work_performed && (
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <div className="text-[9px] font-black uppercase tracking-widest text-[#8B93A7] mb-1">Closing Notes</div>
                    <p className="text-sm text-[#0F1F3A] whitespace-pre-wrap">{job.completion_notes}</p>
                  </div>
                )}
              </Card>
            )}

            {/* Receipts / Materials */}
            {(receipts.length > 0 || (job.material_runs || []).length > 0) && (
              <Card testId="v2body-receipts">
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="text-lg font-black text-[#0F1F3A]">Receipts · Materials</h3>
                  <span className="text-[10px] font-black uppercase tracking-widest text-[#8B93A7]">{receipts.length}</span>
                  <span className="ml-auto text-sm font-black text-[#22C6A6]">
                    ${receipts.reduce((s, r) => s + (Number(r.amount) || 0), 0).toFixed(2)}
                  </span>
                </div>
                <div className="space-y-2">
                  {receipts.map((r, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100">
                      <div className="h-10 w-10 rounded-lg bg-[#F5B93F]/15 flex items-center justify-center text-lg flex-shrink-0">🧾</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-black text-[#0F1F3A] truncate">{r.vendor || r.merchant || "Receipt"}</div>
                        <div className="text-xs text-[#8B93A7] truncate">{r.description || r.notes || r.category || ""}</div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-sm font-black text-[#0F1F3A]">${Number(r.amount || 0).toFixed(2)}</div>
                        <div className="text-[9px] font-black uppercase tracking-widest text-[#8B93A7]">{fmtTimeShort(r.created_at || r.at)}</div>
                      </div>
                    </div>
                  ))}
                  {receipts.length === 0 && (
                    <div className="text-center text-[10px] font-medium text-[#8B93A7] py-4">No receipts logged</div>
                  )}
                </div>
              </Card>
            )}

            {/* Notes moved to top-of-column above Description */}
          </div>

          {/* ═══ RIGHT ═══════════════════════════════════ */}
          <div className="space-y-4">
            {/* Change Status + Add Photos — top slot per user request */}
            <Card testId="v2body-status-actions">
              <div className="flex items-center gap-2 mb-2">
                <div className="text-[9px] font-black uppercase tracking-widest text-[#8B93A7]">Change status</div>
                <StatusFlag status={job.status} size="xs" className="ml-auto"/>
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                {[
                  { k: "assigned",    label: "Assigned",   c: "#FF6A2B" },
                  { k: "in_progress", label: "Active",     c: "#B12FBB" },
                  { k: "follow_up",   label: "Follow-up",  c: "#F5B93F" },
                  { k: "completed",   label: "Complete",   c: "#22C6A6" },
                ].map(s => (
                  <button
                    key={s.k}
                    onClick={() => changeStatus(s.k)}
                    disabled={busy || job.status === s.k}
                    data-testid={`v2-status-${s.k}`}
                    className={`h-9 rounded-xl text-[10px] font-black uppercase tracking-widest transition-colors ${
                      job.status === s.k
                        ? "text-white cursor-default"
                        : "hover:opacity-80"
                    }`}
                    style={{
                      background: job.status === s.k ? s.c : s.c + "12",
                      color: job.status === s.k ? "#fff" : s.c,
                      border: `2px solid ${s.c}${job.status === s.k ? "" : "33"}`,
                    }}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-2 gap-2">
                {/* Gallery/video picker — no `capture` attr so the OS shows
                    the full picker (photo + video, camera roll + files). */}
                <label className="block cursor-pointer" data-testid="v2-add-photos-label">
                  <input
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    onChange={e => uploadPhotos(e.target.files)}
                    className="hidden"
                  />
                  <div className="flex items-center justify-center gap-2 h-10 px-2 rounded-xl bg-gradient-to-r from-[#0F1F3A] to-[#1a2d4f] text-white font-black text-xs hover:from-[#1a2d4f] hover:to-[#243651] transition-colors">
                    <span className="text-base">🖼️</span>
                    <span>From Gallery</span>
                  </div>
                </label>
                {/* Camera-only shortcut — opens the phone camera directly for
                    a quick shoot without hunting through the gallery. */}
                <label className="block cursor-pointer" data-testid="v2-add-photos-camera-label">
                  <input
                    type="file"
                    accept="image/*,video/*"
                    capture="environment"
                    onChange={e => uploadPhotos(e.target.files)}
                    className="hidden"
                  />
                  <div className="flex items-center justify-center gap-2 h-10 px-2 rounded-xl bg-[#00C4CC] hover:bg-[#00e5ed] text-[#0F1F3A] font-black text-xs transition-colors">
                    <span className="text-base">📸</span>
                    <span>Camera</span>
                  </div>
                </label>
              </div>
            </Card>

            {/* Quick actions */}
            <Card testId="v2body-quick-actions" pad="sm">
              <div className="flex items-center gap-2 mb-2 px-1">
                <div className="text-[9px] font-black uppercase tracking-widest text-[#8B93A7]">Quick actions</div>
                <button
                  onClick={() => setDrawerOpen(true)}
                  data-testid="v2-open-more-actions"
                  className="ml-auto h-6 px-2 rounded-full bg-[#0F1F3A] text-white text-[9px] font-black uppercase tracking-widest hover:bg-[#1a2d4f] inline-flex items-center gap-1"
                >
                  ⋮ More
                </button>
              </div>
              <div className="grid grid-cols-3 gap-1">
                <ActionTile icon="📞" label="Call" onClick={call} disabled={!job.customer_phone}/>
                <ActionTile icon="💬" label="SMS" onClick={sms} disabled={!job.customer_phone}/>
                <ActionTile icon="🗺" label="Directions" onClick={directions} disabled={!job.property_address}/>
                <ActionTile
                  icon="🔄"
                  label={syncingCU ? "Syncing…" : "Sync CU"}
                  onClick={async () => {
                    if (syncingCU) return;
                    setSyncingCU(true);
                    try {
                      const r = await api.post(`/clickup/jobs/${id}/sync-now`);
                      const msg = r.data?.message || r.data?.summary || "ClickUp synced";
                      toast.success(msg);
                      reload();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Sync failed");
                    } finally { setSyncingCU(false); }
                  }}
                  disabled={syncingCU}
                />
                {job.appfolio_link && (
                  <ActionTile icon="🏦" label="AppFolio" onClick={() => window.open(job.appfolio_link, "_blank")}/>
                )}
                <ActionTile icon="🖨" label="Print" onClick={() => window.print()}/>
                <ActionTile icon="↗️" label="V1" onClick={() => nav(`/admin/jobs/${id}?v=classic`)}/>
              </div>
            </Card>

            {/* Details */}
            <Card testId="v2body-details">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-black text-[#0F1F3A]">Details</h3>
              </div>
              <div className="space-y-3">
                <MetaRow label="Status" val={<StatusFlag status={job.status} size="sm"/>}/>
                {job.priority && (
                  <MetaRow label="Priority" val={
                    <span className="inline-flex items-center gap-1 h-6 px-2 rounded-full text-[10px] font-black uppercase tracking-widest"
                      style={{ background: (priColors[job.priority] || "#8B93A7") + "22", color: priColors[job.priority] || "#8B93A7" }}>
                      {(job.priority === "urgent" || job.priority === "critical") && "🔥 "}
                      {job.priority}
                    </span>
                  }/>
                )}
                <MetaRow label="Assignee" val={
                  job.assigned_to_name ? (
                    <div className="flex items-center gap-2">
                      <div className="h-6 w-6 rounded-full bg-gradient-to-br from-[#3B82F6] to-[#5B3AE0] flex items-center justify-center text-white font-black text-[10px] flex-shrink-0">
                        {initials(job.assigned_to_name)}
                      </div>
                      <span className="text-sm font-black text-[#0F1F3A]">{job.assigned_to_name}</span>
                    </div>
                  ) : <span className="text-sm font-bold text-[#F97316]">Unassigned</span>
                }/>
                <MetaRow label="Sweeper Pin" val={
                  job.sweeper_pin ? (
                    <div className="flex items-center gap-2 flex-wrap" data-testid="v2body-sweeper-pin-badge">
                      <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-full bg-amber-100 text-amber-700">
                        <PushPin weight="fill" size={11}/>
                        {job.sweeper_pin.truck_label || "Pinned"} · {job.sweeper_pin.date || ""}
                      </span>
                      <PinToSweeperButton jobIds={[id]} workers={workers} onPinned={reload}/>
                    </div>
                  ) : (
                    <div data-testid="v2body-sweeper-pin-btn">
                      <PinToSweeperButton jobIds={[id]} workers={workers} onPinned={reload}/>
                    </div>
                  )
                }/>
                {job.helper_name && (
                  <MetaRow label="Helper" val={<span className="text-sm font-bold text-[#0F1F3A]">{job.helper_name}</span>}/>
                )}
                <MetaRow label="Tenant" val={
                  <div>
                    <div className="text-sm font-black text-[#0F1F3A]">{job.tenant_name || job.customer_name || "—"}</div>
                    {job.customer_phone && <div className="text-[11px] text-[#8B93A7]">{job.customer_phone}</div>}
                  </div>
                }/>
                <MetaRow label="Category" val={<span className="text-sm font-bold text-[#0F1F3A]">{job.category || "—"}</span>}/>
                {job.portfolio_name && (
                  <MetaRow label="Portfolio" val={
                    <span className="inline-flex items-center gap-1.5 text-sm font-black text-[#0F1F3A]">
                      {job.portfolio_color && <span className="h-2.5 w-2.5 rounded-full" style={{ background: job.portfolio_color }}/>}
                      {job.portfolio_name}
                    </span>
                  }/>
                )}
                {job.appfolio_link && (
                  <MetaRow label="AppFolio" val={
                    <a href={job.appfolio_link} target="_blank" rel="noopener" className="text-xs font-bold text-[#00807a] hover:underline">
                      Open in AppFolio →
                    </a>
                  }/>
                )}
                {job.clickup_task_id && (
                  <MetaRow label="ClickUp" val={<span className="text-xs font-bold text-[#00807a]">#{job.work_order_number || job.clickup_task_id.slice(0,8)}</span>}/>
                )}
              </div>
            </Card>

            {/* Timing */}
            <Card testId="v2body-timing">
              <h3 className="text-base font-black text-[#0F1F3A] mb-3">Timing</h3>
              <div className="space-y-2">
                {timing.map((t, i) => (
                  <div key={i} className={`flex items-center gap-2 text-xs ${t.value ? "" : "opacity-40"}`}>
                    <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ background: t.color }}/>
                    <span className="text-[#8B93A7] font-medium">{t.label}</span>
                    <span className="ml-auto font-black text-[#0F1F3A]">{t.value || "—"}</span>
                  </div>
                ))}
              </div>
            </Card>

            {/* Rebill preview */}
            {(job.landlord_quote || job.sub_quoted_price || (job.time_entries || []).length > 0) && (
              <Card testId="v2body-rebill" className="bg-gradient-to-br from-[#22C6A6] to-[#0F7F6A] text-white border-none">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-base font-black">Rebill Preview</h3>
                  <span className="text-[9px] font-black uppercase tracking-widest bg-white/15 px-2 py-0.5 rounded-full">DRAFT</span>
                </div>
                <div className="space-y-1.5 text-sm">
                  {job.landlord_quote?.total != null && <Row2 l="Landlord quote" r={`$${Number(job.landlord_quote.total).toFixed(2)}`}/>}
                  {job.sub_quoted_price != null && <Row2 l="Sub quote" r={`$${Number(job.sub_quoted_price).toFixed(2)}`}/>}
                  {receipts.length > 0 && <Row2 l={`Materials · ${receipts.length}`} r={`$${receipts.reduce((s, r) => s + (Number(r.amount) || 0), 0).toFixed(2)}`}/>}
                </div>
              </Card>
            )}

            {/* Activity */}
            <Card testId="v2body-activity">
              <h3 className="text-base font-black text-[#0F1F3A] mb-3">Activity</h3>
              <div className="relative space-y-3">
                <div className="absolute left-[15px] top-2 bottom-2 w-px bg-slate-200"/>
                {activity.map((t, i) => (
                  <div key={i} className="flex gap-3 relative">
                    <div className="h-8 w-8 rounded-full flex items-center justify-center text-sm flex-shrink-0 relative z-10 ring-4 ring-white"
                      style={{ background: t.color + "22", color: t.color }}>
                      <span>{t.icon}</span>
                    </div>
                    <div className="flex-1 min-w-0 pt-1">
                      <div className="text-xs text-[#0F1F3A] font-semibold leading-snug">{t.text}</div>
                      <div className="text-[10px] font-black uppercase tracking-widest text-[#8B93A7] mt-0.5">{t.when}</div>
                    </div>
                  </div>
                ))}
                {activity.length === 0 && (
                  <div className="text-[11px] text-[#8B93A7] font-medium">No timeline events yet</div>
                )}
              </div>
            </Card>
          </div>
        </div>

        {/* Prominent "More Actions" bar (both a shortcut + a hint) */}
        <div className="mt-6">
          <button
            onClick={() => setDrawerOpen(true)}
            data-testid="v2-open-more-actions-bar"
            className="w-full bg-white rounded-2xl shadow-[0_8px_30px_rgb(15,31,58,0.10)] p-4 flex items-center gap-3 hover:shadow-lg transition-shadow group"
          >
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#0F1F3A] to-[#1a2d4f] flex items-center justify-center text-white text-lg flex-shrink-0">
              ⋮
            </div>
            <div className="text-left flex-1 min-w-0">
              <div className="text-sm font-black text-[#0F1F3A]">All actions &amp; details</div>
              <div className="text-[11px] font-medium text-[#8B93A7]">
                Assign · Change status · Docs &amp; PDFs · ClickUp sync · Tenant links · Habitability · AI estimate · Delete
              </div>
            </div>
            <span className="text-[10px] font-black uppercase tracking-widest text-[#00807a] group-hover:text-[#0F1F3A]">Open →</span>
          </button>
        </div>
      </div>

      {/* Right-side drawer with V1 detail body */}
      {drawerOpen && (
        <div className="fixed inset-0 z-[100]" data-testid="v2-actions-drawer">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
          />
          <aside className="absolute right-0 top-0 h-full w-full lg:max-w-[900px] xl:max-w-[1100px] bg-slate-50 shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-200">
            <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-2">
              <div>
                <div className="text-[9px] font-black uppercase tracking-widest text-[#8B93A7]">Full toolbox</div>
                <div className="text-sm font-black text-[#0F1F3A]">All actions &amp; details</div>
              </div>
              <button
                onClick={() => setDrawerOpen(false)}
                data-testid="v2-close-drawer"
                className="ml-auto h-9 w-9 rounded-full hover:bg-slate-100 text-[#0F1F3A] text-xl leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="bg-white">
              <AdminJobDetailBody v2/>
            </div>
          </aside>
        </div>
      )}

      {/* Lightbox — media pinned to top-half of viewport so the caption
          panel + Save button stay reachable on landscape/tablet. Whole
          panel scrolls if the caption grows or actions wrap. */}
      {lightbox && (
        <div className="fixed inset-0 bg-black/90 z-[100] flex items-start sm:items-center justify-center p-3 sm:p-4 overflow-y-auto" onClick={() => setLightbox(null)} data-testid="v2body-lightbox">
          <button className="fixed top-3 right-3 h-10 w-10 rounded-full bg-white/10 backdrop-blur text-white text-2xl hover:bg-white/25 z-[110]">×</button>
          <div className="max-w-4xl w-full my-auto py-2" onClick={e => e.stopPropagation()}>
            {lightbox.media_type === "video" ? (
              <video src={lightbox.url} controls autoPlay playsInline
                className="rounded-2xl w-auto max-h-[45vh] sm:max-h-[55vh] mx-auto bg-black"/>
            ) : (
              <img src={lightbox.url} alt="" className="rounded-2xl w-auto max-h-[45vh] sm:max-h-[55vh] mx-auto"/>
            )}
            <div className="text-center text-white/80 mt-2 text-sm font-medium capitalize">{lightbox.kind} · {fmtTimeShort(lightbox.at)}</div>
            {/* Action row + inline caption editor — CompanyCam-style. */}
            <LightboxActions
              photo={lightbox}
              jobId={id}
              onMarkup={() => { setMarkupPhoto(lightbox); setLightbox(null); }}
              onCaptionSaved={(newCap) => {
                setLightbox(prev => prev ? { ...prev, caption: newCap } : prev);
                reload();
              }}
            />
          </div>
        </div>
      )}

      {/* Photo markup editor — CompanyCam parity. Loads the photo into
          a canvas, tech draws arrows/circles/text, Save flattens and
          uploads as `kind=annotated` (original preserved server-side). */}
      {markupPhoto && (
        <PhotoMarkupModal
          src={markupPhoto.url}
          jobId={id}
          onClose={() => setMarkupPhoto(null)}
          onSaved={() => reload()}
        />
      )}

      {/* Before/After compare slider — drag divider to see the fix. */}
      {baOpen && (() => {
        const beforePic = [...photos].reverse().find(p => p.kind === "before");
        const afterPic  = [...photos].reverse().find(p => p.kind === "after");
        return (
          <div className="fixed inset-0 z-[200] bg-black/95 flex flex-col p-4" onClick={() => setBaOpen(false)} data-testid="v2body-ba-modal">
            <div className="flex items-center gap-2 mb-3">
              <button onClick={() => setBaOpen(false)} className="h-9 w-9 flex items-center justify-center text-white hover:bg-white/10 rounded" data-testid="v2body-ba-close">✕</button>
              <div className="text-white font-black uppercase tracking-widest text-sm">Before / After Compare</div>
              {beforePic && afterPic && (
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    try {
                      const blob = await buildBeforeAfterComparison(beforePic.url, afterPic.url);
                      if (!blob) throw new Error("Couldn't build the image");
                      const fd = new FormData();
                      fd.append("kind", "after");
                      fd.append("file", new File([blob], `compare-${Date.now()}.jpg`, { type: "image/jpeg" }));
                      fd.append("caption", "Before / After comparison");
                      await api.post(`/jobs/${id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                      toast.success("Comparison saved to WO");
                      reload && reload();
                      setBaOpen(false);
                    } catch (err) {
                      toast.error(`Couldn't save: ${err?.response?.data?.detail || err.message}`);
                    }
                  }}
                  data-testid="v2body-ba-save"
                  className="ml-auto h-9 px-3 rounded bg-emerald-500 hover:bg-emerald-600 text-white font-black uppercase tracking-widest text-[10px]">
                  💾 Save Comparison
                </button>
              )}
            </div>
            <div className="flex-1 flex items-center justify-center" onClick={e => e.stopPropagation()}>
              {beforePic && afterPic ? (
                <div className="w-full max-w-4xl">
                  <BeforeAfterSlider
                    before={<img src={beforePic.url} alt="before" className="absolute inset-0 w-full h-full object-cover"/>}
                    after={<img src={afterPic.url} alt="after" className="absolute inset-0 w-full h-full object-cover"/>}
                    className="aspect-[4/3]"
                  />
                  {(beforePic.caption || afterPic.caption) && (
                    <div className="mt-4 grid grid-cols-2 gap-3 text-white/90 text-sm">
                      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                        <div className="text-[9px] font-black uppercase tracking-widest text-amber-300 mb-1">Before</div>
                        <div>{beforePic.caption || <span className="text-white/40 italic">No caption</span>}</div>
                      </div>
                      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
                        <div className="text-[9px] font-black uppercase tracking-widest text-emerald-300 mb-1">After</div>
                        <div>{afterPic.caption || <span className="text-white/40 italic">No caption</span>}</div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-white/70 text-center">
                  <div className="text-5xl mb-3">📷</div>
                  <div className="font-black">Need at least one Before + one After.</div>
                </div>
              )}
            </div>
          </div>
        );
      })()}
      {/* Share result — offers copy + email-to-landlord after minting a link. */}
      {shareResult && (
        <ShareResultModal
          share={shareResult}
          onClose={() => setShareResult(null)}
        />
      )}

      {/* AI Bid Drafter — admin-only, generates a photo-backed bid draft
          via Claude Vision, lets the office edit + email to landlord for
          typed-signature approval. */}
      {bidDrafterOpen && (
        <BidDrafterModal
          jobId={id}
          jobTitle={job?.title || ""}
          onClose={() => setBidDrafterOpen(false)}
          onSaved={reload}
        />
      )}

      {/* Time-lapse preview — MP4 (H.264) with animated GIF fallback if
          the ffmpeg-encoded MP4 can't decode for any reason. */}
      {timelapseOpen && (
        <div className="fixed inset-0 z-[220] bg-black/95 flex flex-col items-center justify-center p-4"
             onClick={() => setTimelapseOpen(false)} data-testid="v2body-timelapse-modal">
          <div className="text-white/80 font-black uppercase tracking-widest text-sm mb-3">🎞️ Time-lapse · {photos.length} frames</div>
          <TimelapsePlayer jobId={id} />
          <div className="text-white/40 text-[10px] font-black uppercase tracking-widest mt-3">Long-press / right-click to save</div>
          <button onClick={() => setTimelapseOpen(false)}
            data-testid="v2body-timelapse-close"
            className="mt-3 h-9 px-4 rounded-full bg-white/10 hover:bg-white/20 text-white font-black uppercase tracking-widest text-xs">Close</button>
        </div>
      )}

      {/* InspectRadar Grid overlay — opens from the pink Grid button in the header */}
      {gridInspection && (
        <InspectRadarGridOverlay jobId={id} job={job} phase={gridInspection}
          onClose={() => setGridInspection(null)}
          onJobReload={async () => { try { const r = await api.get(`/jobs/${id}`); setJob(r.data); } catch {} }}/>
      )}
    </AdminShell>
  );
}

function InspectRadarGridOverlay({ jobId, job, phase, onClose, onJobReload }) {
  // Mount a lightweight wrapper that fetches the inspection, builds the
  // rooms array (with photos), and renders the shared InspectRadarGrid.
  const [state, setState] = useState({ rooms: [], loading: true });
  const load = async () => {
    try {
      const r = await api.get(`/jobs/${jobId}/inspection/${phase}`);
      setState({ rooms: r.data?.rooms || [], loading: false });
    } catch (e) {
      setState({ rooms: [], loading: false });
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [jobId, phase]);

  const photoField = phase === "move_out" ? "before_photos" : "after_photos";
  const photosByRoom = (job?.[photoField] || []).reduce((acc, p) => {
    if (!p.inspection_room) return acc;
    (acc[p.inspection_room] = acc[p.inspection_room] || []).push(p);
    return acc;
  }, {});
  const rooms = state.rooms.map(r => ({ ...r, _photos: photosByRoom[r.room] || [] }));

  return (
    <div className="fixed inset-0 z-[80] bg-white text-slate-900 flex flex-col" data-testid="v2-grid-overlay">
      <div className="bg-[#0f172a] text-white px-4 h-14 flex items-center gap-2 shrink-0">
        <button onClick={onClose} className="h-9 w-9 rounded-full flex items-center justify-center hover:bg-white/10"
          data-testid="v2-grid-overlay-close">✕</button>
        <div className="font-black uppercase tracking-widest text-sm">
          InspectRadar · Grid · {phase.replace("_", "-")}
        </div>
        <span className="text-white/60 text-xs ml-2 truncate">{job?.property_address || ""}</span>
      </div>
      <div className="flex-1 overflow-y-auto">
        {state.loading ? (
          <div className="text-center py-12 text-slate-400">Loading…</div>
        ) : (
          <InspectRadarGrid jobId={jobId} phase={phase} rooms={rooms}
            onRoomsChanged={async () => { await load(); await onJobReload(); }}/>
        )}
      </div>
    </div>
  );
}

/**
 * ShareResultModal — after the admin mints a public share URL, this
 * modal offers a copy button, the raw URL for pasting into SMS/Slack,
 * and a one-tap "Email to landlord" form that hits `/photo-shares/email`.
 */
function ShareResultModal({ share, onClose }) {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const doSend = async () => {
    if (!email.trim()) return;
    setSending(true);
    try {
      await api.post("/photo-shares/email", { token: share.token, to_email: email.trim(), message: msg });
      setSent(true);
      toast.success(`Emailed to ${email}`);
      setTimeout(onClose, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send email");
    } finally { setSending(false); }
  };
  return (
    <div className="fixed inset-0 z-[220] bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4"
         onClick={onClose} data-testid="share-result-modal">
      <div className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[90vh] flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b-2 border-slate-900 bg-slate-50 flex items-center gap-2">
          <div className="font-display font-black text-base uppercase tracking-tight flex-1">
            🔗 Photos are shareable
          </div>
          <button onClick={onClose} data-testid="share-result-close"
            className="h-9 w-9 flex items-center justify-center text-slate-500 hover:text-slate-800">×</button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Public URL (already copied)</div>
            <div className="flex gap-2">
              <input readOnly value={share.fullUrl}
                data-testid="share-result-url"
                onFocus={(e) => e.target.select()}
                className="flex-1 h-10 px-3 border-2 border-slate-300 rounded-md text-xs font-mono bg-slate-50 text-[#0F1F3A]"/>
              <button onClick={() => { navigator.clipboard.writeText(share.fullUrl); toast.success("Copied"); }}
                data-testid="share-result-copy"
                className="h-10 px-3 bg-slate-900 hover:bg-slate-800 text-white font-black uppercase tracking-widest text-[10px] rounded-md">
                Copy
              </button>
            </div>
            <div className="text-[10px] text-slate-400 font-bold mt-1">
              Expires {new Date(share.expires_at).toLocaleDateString()}
            </div>
          </div>
          {/* Email-to-landlord — disabled during beta. Kept visible (greyed out) as a
              preview of what's coming; not clickable and doesn't hit the backend. */}
          <div className="border-t-2 border-slate-100 pt-4 opacity-60 pointer-events-none select-none" aria-disabled="true">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">📧 Email to landlord</div>
              <span className="text-[9px] font-black uppercase tracking-widest bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">Coming Soon</span>
            </div>
            <input type="email" value="" readOnly disabled
              placeholder="landlord@example.com"
              data-testid="share-result-email"
              className="w-full h-10 px-3 border-2 border-slate-300 rounded-md text-sm font-medium mb-2 bg-slate-100 text-slate-500 cursor-not-allowed"/>
            <textarea value="" readOnly disabled
              placeholder="Optional note to include… ('Here are the finished photos from Tuesday.')"
              rows={3}
              data-testid="share-result-message"
              className="w-full px-3 py-2 border-2 border-slate-300 rounded-md text-sm font-medium resize-none bg-slate-100 text-slate-500 cursor-not-allowed"/>
            <button type="button" disabled tabIndex={-1}
              data-testid="share-result-send"
              className="mt-3 w-full h-11 bg-slate-300 text-slate-500 font-black uppercase tracking-widest text-xs rounded-md cursor-not-allowed">
              Send Email
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * TimelapsePlayer — fetches the H.264 MP4 with the auth header, falls
 * back to the animated GIF endpoint if the MP4 route errors out (older
 * server without ffmpeg, encode failure, etc). Keeps auth token out of
 * the URL because Twitter/Instagram sharers strip query strings.
 */
function TimelapsePlayer({ jobId }) {
  const [state, setState] = useState({ url: null, kind: null, error: null });
  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    (async () => {
      // Try MP4 first
      try {
        const r = await api.get(`/jobs/${jobId}/timelapse.mp4`, { responseType: "blob" });
        if (cancelled) return;
        objectUrl = URL.createObjectURL(r.data);
        setState({ url: objectUrl, kind: "mp4", error: null });
      } catch (mp4Err) {
        try {
          const r2 = await api.get(`/jobs/${jobId}/timelapse.gif`, { responseType: "blob" });
          if (cancelled) return;
          objectUrl = URL.createObjectURL(r2.data);
          setState({ url: objectUrl, kind: "gif", error: null });
        } catch (gifErr) {
          if (!cancelled) setState({ url: null, kind: null, error: gifErr?.response?.data?.detail || "Couldn't build time-lapse" });
        }
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [jobId]);
  if (state.error) {
    return <div className="text-red-300 text-sm font-bold" data-testid="v2body-timelapse-error">{state.error}</div>;
  }
  if (!state.url) {
    return <div className="text-white/60 text-sm font-black uppercase tracking-widest animate-pulse" data-testid="v2body-timelapse-loading">Encoding…</div>;
  }
  if (state.kind === "mp4") {
    return (
      <video
        src={state.url}
        autoPlay loop muted playsInline controls
        data-testid="v2body-timelapse-mp4"
        className="max-h-[75vh] rounded-2xl shadow-2xl bg-slate-900"
        onClick={(e) => e.stopPropagation()}
      />
    );
  }
  return (
    <img
      src={state.url}
      alt="timelapse"
      data-testid="v2body-timelapse-gif"
      className="max-h-[75vh] rounded-2xl shadow-2xl bg-slate-900"
      onClick={(e) => e.stopPropagation()}
    />
  );
}

/**
 * LightboxActions — row of buttons + inline caption editor beneath a
 * photo in the lightbox. Split out because it holds its own text state
 * and would force the whole detail page to re-render on every keystroke
 * if inlined above.
 */
function LightboxActions({ photo, jobId, onMarkup, onCaptionSaved }) {
  const [captionDraft, setCaptionDraft] = useState(photo?.caption || "");
  const [savingCap, setSavingCap] = useState(false);
  useEffect(() => { setCaptionDraft(photo?.caption || ""); }, [photo?.file_id]);
  const dirty = (captionDraft || "").trim() !== (photo?.caption || "").trim();
  const saveCaption = async () => {
    if (!photo?.file_id) return;
    setSavingCap(true);
    try {
      const { data } = await api.patch(`/jobs/${jobId}/photo/${photo.file_id}/caption`, {
        caption: captionDraft,
      });
      onCaptionSaved && onCaptionSaved(data.caption || "");
    } catch (e) {
      // Toast surfaces from parent; keep console silent to avoid noise
    } finally {
      setSavingCap(false);
    }
  };
  return (
    <div className="mt-4 max-w-2xl mx-auto space-y-3">
      <div className="flex items-center justify-center gap-2 flex-wrap">
        {photo?.file_id && (
          <button onClick={onMarkup}
            data-testid="v2body-lightbox-markup"
            className="h-10 px-4 rounded-full bg-[#00C4CC] hover:bg-[#00e5ed] text-[#0F1F3A] font-black uppercase tracking-widest text-xs inline-flex items-center gap-2">
            ✏️ Markup Photo
          </button>
        )}
        <a href={photo.url} target="_blank" rel="noreferrer"
          data-testid="v2body-lightbox-fullres"
          className="h-10 px-4 rounded-full bg-white/10 hover:bg-white/20 text-white font-black uppercase tracking-widest text-xs inline-flex items-center gap-2">
          Full Size ↗
        </a>
      </div>
      {photo?.voice_note?.path && (
        <div className="rounded-xl bg-purple-500/10 border border-purple-500/30 p-3" data-testid="v2body-lightbox-voice-row">
          <div className="text-[9px] font-black uppercase tracking-widest text-purple-200 mb-1.5 flex items-center gap-2">
            🎙️ Voice Note {photo.voice_note.by ? `· from ${photo.voice_note.by}` : ""}
          </div>
          <audio controls preload="metadata"
            data-testid="v2body-lightbox-voice-audio"
            src={`${API}/files/${photo.voice_note.path}?auth=${encodeURIComponent(localStorage.getItem('access_token') || '')}`}
            className="w-full h-9"/>
        </div>
      )}
      {photo?.file_id && (
        <div className="rounded-xl bg-white/5 border border-white/10 p-3" data-testid="v2body-lightbox-caption-row">
          <div className="text-[9px] font-black uppercase tracking-widest text-white/60 mb-1.5">Caption</div>
          <textarea
            value={captionDraft}
            onChange={(e) => setCaptionDraft(e.target.value.slice(0, 500))}
            placeholder="Describe this photo — 'shut-off valve, kitchen sink'"
            data-testid="v2body-lightbox-caption-input"
            rows={2}
            className="w-full bg-white/10 text-white placeholder-white/40 rounded-md px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#00C4CC] resize-none"/>
          <div className="flex items-center gap-2 mt-2">
            {photo.caption_by && !dirty && (
              <div className="text-[10px] text-white/50 font-bold">
                Set by {photo.caption_by}
              </div>
            )}
            <div className="ml-auto text-[10px] text-white/40 font-bold">{captionDraft.length}/500</div>
            <button
              onClick={saveCaption}
              disabled={!dirty || savingCap}
              data-testid="v2body-lightbox-caption-save"
              className="h-8 px-3 rounded-md bg-[#00C4CC] hover:bg-[#00e5ed] disabled:opacity-50 text-[#0F1F3A] font-black uppercase tracking-widest text-[10px]">
              {savingCap ? "Saving…" : dirty ? "Save Caption" : "Saved"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── helpers ─────────────────────────────────────────── */

function Card({ children, testId, pad = "md", className = "" }) {
  const p = pad === "sm" ? "p-3" : pad === "lg" ? "p-6" : "p-5";
  return (
    <div data-testid={testId}
      className={`bg-white rounded-2xl shadow-[0_8px_30px_rgb(15,31,58,0.10)] ${p} ${className}`}>
      {children}
    </div>
  );
}

function MetaRow({ label, val }) {
  return (
    <div className="flex items-start gap-3">
      <div className="text-[9px] font-black uppercase tracking-widest text-[#8B93A7] w-20 flex-shrink-0 pt-1">{label}</div>
      <div className="flex-1 min-w-0">{val}</div>
    </div>
  );
}

function ActionTile({ icon, label, onClick, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled}
      className={`flex flex-col items-center gap-1 p-2 rounded-xl transition-colors ${
        disabled ? "opacity-40 cursor-not-allowed" : "hover:bg-slate-50"
      }`}>
      <span className="text-xl leading-none">{icon}</span>
      <span className="text-[9px] font-black uppercase tracking-widest text-[#0F1F3A]">{label}</span>
    </button>
  );
}

function Row2({ l, r }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-white/90">{l}</span>
      <span className="text-white font-semibold">{r}</span>
    </div>
  );
}

function normPhoto(p) {
  if (typeof p === "string") return { url: mediaSrc(p), at: null, checklist_id: null, file_id: null, media_type: "image" };
  const ct = (p.content_type || "").toLowerCase();
  const inferredVideo = p.media_type === "video" || ct.startsWith("video/")
    || /\.(mp4|mov|webm|avi|m4v)$/i.test(p.path || p.storage_path || "");
  return {
    url: mediaSrc(p),
    at: p.at || p.uploaded_at || p.created_at || null,
    checklist_id: p.checklist_id || null,
    file_id: p.file_id || null,
    tags: p.tags || [],
    source: p.source || null,
    caption: p.caption || "",
    caption_by: p.caption_by || "",
    caption_at: p.caption_at || null,
    annotated: p.annotated || false,
    ai_tags: p.ai_tags || [],
    ai_note: p.ai_note || "",
    kind: p.kind || null,
    voice_note: p.voice_note || null,
    media_type: inferredVideo ? "video" : "image",
    content_type: p.content_type || "",
  };
}

function initials(name) {
  if (!name) return "?";
  return name.trim().split(/\s+/).map(w => w[0]).slice(0, 2).join("").toUpperCase();
}

// System / integration senders don't have a human name; give them a friendly
// label + avatar so the note thread reads clean instead of "Unknown".
function noteAuthorLabel(n) {
  if (n?.by_name) return n.by_name;
  const by = (n?.by || "").toLowerCase();
  const source = (n?.source || "").toLowerCase();
  const kind = (n?.kind || "").toLowerCase();
  if (by === "clickup" || source === "clickup" || kind.startsWith("clickup")) return "ClickUp Sync";
  if (by === "appfolio" || source === "appfolio" || source === "appfolio_csv") return "AppFolio Sync";
  if (by === "kraaft"  || source === "kraaft") return "Kraaft Sync";
  if (by === "twilio"  || source === "twilio") return "Twilio";
  if (by === "resend"  || source === "resend" || source === "email") return "Email";
  if (by === "system"  || source === "system") return "System";
  if (by === "agent"   || source === "agent")  return "ProMan Agent";
  // Fall back to "Field" (matches JobDetail.jsx pattern) rather than "Unknown"
  return "Field";
}
function noteAvatarBg(n) {
  const by = (n?.by || "").toLowerCase();
  const source = (n?.source || "").toLowerCase();
  const kind = (n?.kind || "").toLowerCase();
  if (by === "clickup" || source === "clickup" || kind.startsWith("clickup"))
    return "bg-gradient-to-br from-[#7B68EE] to-[#4A3CBB]";  // ClickUp purple
  if (by === "appfolio" || source === "appfolio_csv" || source === "appfolio")
    return "bg-gradient-to-br from-[#0057D9] to-[#003C99]";  // AppFolio blue
  if (by === "system" || source === "system")
    return "bg-gradient-to-br from-slate-500 to-slate-700";
  return "bg-gradient-to-br from-[#FF6A2B] to-[#B12FBB]";
}
function noteAvatarContent(n) {
  if (n?.by_name) return initials(n.by_name);
  const by = (n?.by || "").toLowerCase();
  const source = (n?.source || "").toLowerCase();
  const kind = (n?.kind || "").toLowerCase();
  if (by === "clickup" || source === "clickup" || kind.startsWith("clickup")) return "CU";
  if (by === "appfolio" || source === "appfolio_csv" || source === "appfolio") return "AF";
  if (by === "kraaft" || source === "kraaft") return "KR";
  if (by === "twilio" || source === "twilio") return "TW";
  if (by === "system" || source === "system") return "SYS";
  if (by === "agent" || source === "agent") return "PM";
  return "•";
}

function fmtTimeShort(ts) {
  if (!ts) return "";
  try {
    const d = new Date(ts);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    const daysAgo = Math.floor((now - d) / 86400000);
    if (daysAgo < 7) return d.toLocaleDateString([], { weekday: "short" }) + " " + d.toLocaleTimeString([], { hour: "numeric" });
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
  } catch { return ""; }
}

function buildTiming(j) {
  if (!j) return [];
  return [
    { label: "Created",   value: fmtTimeShort(j.created_at),  color: "#00C4CC" },
    { label: "Assigned",  value: j.assigned_to_name ? fmtTimeShort(j.assigned_at || j.created_at) : null, color: "#FF6A2B" },
    { label: "En route",  value: fmtTimeShort(j.en_route_at), color: "#F97316" },
    { label: "Clocked in", value: fmtTimeShort(j.clock_in_at), color: "#B12FBB" },
    { label: "Clocked out", value: fmtTimeShort(j.clock_out_at), color: "#8B5CF6" },
    { label: "Completed", value: fmtTimeShort(j.completed_at), color: "#22C6A6" },
  ];
}

function buildActivity(j) {
  if (!j) return [];
  const events = [];
  if (j.created_at)   events.push({ icon: "📥", color: "#3B82F6", text: `Created via ${j.source || "manual"}`, when: fmtTimeShort(j.created_at) });
  if (j.assigned_to_name && j.assigned_at) events.push({ icon: "👷", color: "#FF6A2B", text: `Assigned to ${j.assigned_to_name}`, when: fmtTimeShort(j.assigned_at) });
  if (j.en_route_at)  events.push({ icon: "🚚", color: "#F97316", text: `${j.en_route_by_name || "Tech"} en route`, when: fmtTimeShort(j.en_route_at) });
  if (j.clock_in_at)  events.push({ icon: "⏱", color: "#B12FBB", text: "Clocked in on-site", when: fmtTimeShort(j.clock_in_at) });
  const noteCount = (j.notes || []).length;
  if (noteCount > 0)  events.push({ icon: "📝", color: "#8B5CF6", text: `${noteCount} note${noteCount === 1 ? "" : "s"} on file`, when: "" });
  const photoCount = (j.before_photos || []).length + (j.after_photos || []).length;
  if (photoCount > 0) events.push({ icon: "📸", color: "#22C6A6", text: `${photoCount} photo${photoCount === 1 ? "" : "s"} uploaded`, when: "" });
  if (j.clock_out_at) events.push({ icon: "⏱", color: "#8B5CF6", text: "Clocked out", when: fmtTimeShort(j.clock_out_at) });
  if (j.completed_at) events.push({ icon: "✓",  color: "#22C6A6", text: "Completed", when: fmtTimeShort(j.completed_at) });
  return events.reverse();
}
